/**
 * The rules behind the profile panel on a company page.
 *
 * Named for the concern rather than the component, like lifecycleRules.ts
 * beside LifecyclePanel.tsx -- and note the case: `companyProfile.ts` and
 * `CompanyProfilePanel.tsx` differ by more than case on purpose. Two files
 * differing only in case are one file on macOS and two on Linux, and tsc
 * refuses the program outright (TS1149) before anything can run.
 *
 * WHY THIS EXISTS AT ALL
 * ----------------------
 * ops#2: four facts the CRM had nowhere to put -- what a company is to us,
 * what kind of customer, how many barrels, and whether that number is known
 * or guessed. Migration 069 gave them a table; this file is the half of the
 * answer that has to be right before anything is written to it.
 *
 * THE ONE IDEA EVERYTHING HERE SERVES
 * -----------------------------------
 * A BARREL COUNT MEANS DIFFERENT THINGS BY CUSTOMER TYPE. A distillery's is a
 * lifetime TOTAL; an NDP's is barrels a YEAR. Those differ by orders of
 * magnitude, so "12,000 barrels" on its own is not a fact -- it is two
 * completely different companies rendered identically. Hence describeBarrels
 * below, which is the only way this app is allowed to print a count: it
 * cannot render the number without its basis, because there is no code path
 * that returns one without the other.
 *
 * The basis is STORED rather than derived because the third customer type,
 * "other", implies no basis at all; deriving one there would mean inventing
 * it, and an invented basis is worse than a missing one because it looks
 * answered.
 *
 * WHY THE RULES ARE HERE AND NOT IN THE COMPONENT
 * -----------------------------------------------
 * The `marketing` vitest project globs plain `.ts` and runs in Node; `app`
 * runs in real Chromium and costs a ~130 MB Playwright download. Every rule
 * below either builds a write that five CHECK constraints will reject, or
 * decides what a person reads about a number they will size a deal on.
 * Neither needs a DOM, and neither should sit one browser install away from
 * being skipped.
 *
 * THE CONSTRAINTS THESE FUNCTIONS EXIST TO SATISFY (069)
 * ------------------------------------------------------
 *   company_profile_customer_type_scope   a customer type only on a customer
 *   company_profile_barrels_scope         a barrel count only on a customer
 *   company_profile_barrels_need_basis    a count must have a basis
 *   company_profile_distillery_is_total   a distillery's basis is total
 *   company_profile_ndp_is_annual         an NDP's basis is annual
 *
 * Every one of them rejects the whole write, so a panel that changed `kind`
 * without shedding the customer-only columns in the SAME patch would show a
 * Postgres error to somebody doing something entirely reasonable.
 */

import type { DataProvider, Identifier } from "ra-core";
import type { Invalidator } from "./touchActions";
import type {
  BarrelBasis,
  CompanyKind,
  CompanyProfileRow,
  CustomerType,
} from "./types";

/**
 * The pickers' contents, in the order ops#2 lists them.
 *
 * Written here rather than inline in the component so the labels a person
 * reads and the values the constraints accept cannot drift apart -- and so a
 * test can assert the pair.
 */
export const COMPANY_KINDS: { value: CompanyKind; label: string }[] = [
  { value: "customer", label: "Customer" },
  { value: "partner", label: "Partner" },
  { value: "other", label: "Other" },
];

export const CUSTOMER_TYPES: { value: CustomerType; label: string }[] = [
  { value: "distillery", label: "Distillery" },
  // Spelled out: NDP is not a word outside this industry, and the panel is
  // read by people who did not write the schema.
  { value: "ndp", label: "NDP (non-distilling producer)" },
  { value: "other", label: "Other" },
];

/**
 * The two bases, labeled as the QUESTION rather than as the column value.
 * "total" and "annual" are what Postgres holds; "a lifetime total" and
 * "barrels a year" are what somebody can answer.
 */
export const BARREL_BASES: { value: BarrelBasis; label: string }[] = [
  { value: "total", label: "A lifetime total — barrels held" },
  { value: "annual", label: "Barrels a year" },
];

/**
 * `barrel_count` is an `int`, not a bigint. 2,147,483,648 does not store as a
 * large number, it fails -- so it is refused here with a sentence rather than
 * arriving as an overflow error on somebody's typo.
 */
export const BARREL_COUNT_MAX = 2147483647;

/** The columns this panel is allowed to write. */
export type CompanyProfilePatch = Partial<
  Pick<
    CompanyProfileRow,
    | "kind"
    | "customer_type"
    | "barrel_count"
    | "barrel_count_basis"
    | "barrel_count_is_estimate"
    | "notes"
  >
>;

/* ── the basis ─────────────────────────────────────────────────────────── */

/**
 * The basis a customer type FORCES, or null when it forces none.
 *
 * The two forced cases mirror company_profile_distillery_is_total and
 * company_profile_ndp_is_annual exactly, which is why the panel shows them as
 * facts rather than as choices: offering a distillery the option of "annual"
 * would be offering a write the database refuses.
 *
 * "other" returns null and MUST. It is the whole reason barrel_count_basis is
 * a stored column: there is no basis to derive for a customer that is neither
 * a distillery nor an NDP, so the person has to say.
 */
export function impliedBasis(
  customerType: CustomerType | null | undefined,
): BarrelBasis | null {
  if (customerType === "distillery") return "total";
  if (customerType === "ndp") return "annual";
  return null;
}

/**
 * Write a basis for a customer type that implies none.
 *
 * THROWS on one that contradicts the type. The database would refuse it too,
 * but that refusal arrives as a constraint name in a toast -- and this is the
 * second guard behind a panel that already renders the basis as a fact for
 * distilleries and NDPs. A picker is a suggestion; this is what reaches the
 * database.
 */
export function basisPatch(
  basis: BarrelBasis,
  customerType: CustomerType | null | undefined,
): CompanyProfilePatch {
  const forced = impliedBasis(customerType);
  if (forced && forced !== basis)
    throw new Error(
      customerType === "distillery"
        ? "A distillery's barrel count is a lifetime total, not a yearly figure. The two differ by orders of magnitude, so the database refuses the pair."
        : "An NDP's barrel count is barrels a year, not a lifetime total. The two differ by orders of magnitude, so the database refuses the pair.",
    );
  return { barrel_count_basis: basis };
}

/* ── the kind, and what it takes with it ───────────────────────────────── */

/**
 * The patch that moves a company between customer, partner and other.
 *
 * LEAVING `customer` CLEARS THE CUSTOMER-ONLY COLUMNS IN THE SAME WRITE.
 * Three constraints scope them to `kind = 'customer'`, and a CHECK rejects
 * the whole statement -- so a panel that wrote `kind` alone would answer a
 * perfectly reasonable click with a Postgres error naming a constraint the
 * person has never heard of. Clearing them here is not tidiness; it is the
 * only way that write succeeds.
 *
 * `barrel_count_is_estimate` is RESET rather than cleared: it is NOT NULL.
 * Leaving a stale `false` behind would mean the next count somebody types for
 * this company arrives already marked as confirmed, which is exactly the
 * hearsay-into-fact promotion the column's `default true` exists to stop.
 *
 * BECOMING a customer discards nothing. The fields are already empty -- the
 * constraints guaranteed it while the company was not one -- so writing
 * explicit nulls would only risk clearing a profile somebody had just filled
 * in if this ever fired twice.
 */
export function kindPatch(kind: CompanyKind): CompanyProfilePatch {
  if (kind === "customer") return { kind };
  return {
    kind,
    customer_type: null,
    barrel_count: null,
    barrel_count_basis: null,
    barrel_count_is_estimate: true,
  };
}

/**
 * Would this change of kind throw away a barrel count?
 *
 * The panel asks before it does. That number took a conversation to get, and
 * nothing in the app can bring it back -- there is no history table for
 * company_profile, only an updated_at.
 *
 * A RECORDED ZERO COUNTS. `barrel_count >= 0` allows it, and "they hold none"
 * is a finding somebody established, not an empty field.
 */
export function discardsBarrelCount(
  profile: Pick<CompanyProfileRow, "barrel_count"> | null | undefined,
  nextKind: CompanyKind,
): boolean {
  return nextKind !== "customer" && profile?.barrel_count != null;
}

/* ── the customer type, and the basis that travels with it ─────────────── */

/**
 * The patch that sets a customer type, CARRYING THE BASIS WITH IT.
 *
 * Two columns that must move together, like stagePatch's (stage, track). An
 * NDP switched to distillery while barrel_count_basis still says 'annual'
 * violates company_profile_distillery_is_total and the update is rejected
 * outright; and were that constraint ever dropped, the company's yearly run
 * would silently start reading as a lifetime total on every screen.
 *
 * For "other" the recorded basis is KEPT, not cleared: any basis is legal for
 * it, and clearing one while a count exists would break
 * company_profile_barrels_need_basis. When there is nothing to keep the basis
 * stays null, and the panel has to ask before a count can be typed.
 *
 * THROWS when the company is not a customer -- company_profile_customer_type_scope.
 * The panel hides the picker in that case, but a hidden control is still a
 * control something else could fire.
 */
export function customerTypePatch(
  customerType: CustomerType,
  current: { kind: CompanyKind | null; basis: BarrelBasis | null | undefined },
): CompanyProfilePatch {
  if (current.kind !== "customer")
    throw new Error(
      "Only a customer has a customer type. Set the type to Customer first — the database scopes this column to it.",
    );
  return {
    customer_type: customerType,
    barrel_count_basis: impliedBasis(customerType) ?? current.basis ?? null,
  };
}

/* ── the count ─────────────────────────────────────────────────────────── */

export type BarrelCheck =
  | { ok: true; patch: CompanyProfilePatch }
  | { ok: false; reason: string };

/**
 * Can this typed value become a barrel count, and with which basis?
 *
 * RETURNS THE BASIS IN THE SAME PATCH, always. company_profile_barrels_need_basis
 * makes a count without one impossible in the database; this makes it
 * impossible in the app, which is where it would otherwise be built and then
 * bounce back as a constraint error.
 *
 * THE IMPLIED BASIS WINS over whatever the caller is holding. A distillery
 * saved as annual is the single worst outcome available here -- it is not a
 * refused write, it is a plausible one -- so a stale piece of component state
 * cannot produce it even by passing both.
 *
 * EMPTY CLEARS THE COUNT and leaves the basis alone. A number that was wrong
 * and cannot be removed is worse than no number, because it keeps being read.
 */
export function validateBarrelCount(
  raw: string | null | undefined,
  current: {
    kind: CompanyKind | null;
    customerType: CustomerType | null | undefined;
    basis: BarrelBasis | null | undefined;
  },
): BarrelCheck {
  if (current.kind !== "customer")
    return {
      ok: false,
      reason:
        "Only a customer has a barrel count. Set the type to Customer first — the database scopes this column to it.",
    };

  const text = (raw ?? "").trim();
  if (!text) return { ok: true, patch: { barrel_count: null } };

  const count = Number(text);
  if (!Number.isInteger(count) || count < 0)
    return {
      ok: false,
      reason:
        "A barrel count is a whole number of barrels, zero or more. Leave it empty if nobody knows yet.",
    };
  if (count > BARREL_COUNT_MAX)
    return {
      ok: false,
      reason: `${text} is larger than the column can hold. The biggest distillery on record holds a few million barrels, so this is almost certainly a typo.`,
    };

  const basis = impliedBasis(current.customerType) ?? current.basis ?? null;
  if (!basis)
    return {
      ok: false,
      reason:
        "Say what this counts first: a lifetime total, or barrels a year. A distillery's total and an NDP's yearly run differ by orders of magnitude, so a count without its basis cannot be compared with anyone else's.",
    };

  return {
    ok: true,
    patch: { barrel_count: count, barrel_count_basis: basis },
  };
}

/**
 * A barrel count in words: THE NUMBER, WHAT IT COUNTS, AND HOW SURE WE ARE.
 *
 * The only way this app prints a count. There is deliberately no function
 * that returns the number on its own, because "12,000 barrels" is not a fact
 * -- a distillery's 12,000 is a lifetime total and an NDP's is a yearly run,
 * and the two are different companies rendered identically. A caller that
 * wanted to print the figure in a table would have to write that bug itself.
 *
 * ESTIMATED READS AS AN ANSWER, NOT AS MISSING DATA. Almost every one of
 * these numbers arrives from a conversation, `barrel_count_is_estimate`
 * defaults to true for that reason, and a panel that made the default look
 * like an unfinished field would push people to mark guesses as confirmed to
 * make the screen look tidy. So: "an estimate" against "confirmed", both
 * complete sentences about a number we have.
 *
 * The no-basis branch cannot happen in the database -- barrels_need_basis
 * forbids it -- so it can only arrive from a half-built draft in the app. It
 * says the number cannot be compared rather than guessing which it is.
 */
export function describeBarrels(
  profile:
    | Pick<
        CompanyProfileRow,
        "barrel_count" | "barrel_count_basis" | "barrel_count_is_estimate"
      >
    | null
    | undefined,
): string {
  const count = profile?.barrel_count;
  if (count == null)
    return "No barrel count on file — nobody has asked, or nobody has said.";

  const barrels = `${count.toLocaleString("en-US")} ${count === 1 ? "barrel" : "barrels"}`;
  const sureness = profile?.barrel_count_is_estimate
    ? "an estimate"
    : "confirmed";

  if (!profile?.barrel_count_basis)
    return `${barrels} — but nothing here says whether that is a lifetime total or a yearly figure, so it cannot be compared with any other company's.`;

  return profile.barrel_count_basis === "total"
    ? `${barrels} held in total — ${sureness}.`
    : `${barrels} a year — ${sureness}.`;
}

/**
 * The basis, as a sentence about THIS customer type.
 *
 * Used where the basis is a fact rather than a control, which is every
 * distillery and every NDP. Saying "total" as a bare word next to a number
 * would leave the reader to work out that it is a lifetime figure.
 */
export function describeBasis(
  basis: BarrelBasis | null | undefined,
  customerType: CustomerType | null | undefined,
): string {
  if (customerType === "distillery")
    return "A distillery's count is the barrels it holds — a lifetime total.";
  if (customerType === "ndp")
    return "An NDP's count is barrels a year, not a standing inventory.";
  if (basis === "total") return "Counted as a lifetime total — barrels held.";
  if (basis === "annual") return "Counted as barrels a year.";
  return "Nobody has said whether this company's barrels are a lifetime total or a yearly figure. Until they do, a count here could not be compared with anyone else's.";
}

/* ── writing ───────────────────────────────────────────────────────────── */

/**
 * The resources a profile write changes, and therefore what must refetch.
 *
 * SHORT ON PURPOSE, and each entry earns its place. `company_profile` is the
 * row itself. `companies` is the page the panel is rendered inside: what a
 * company IS to us is the headline fact about it, and the record query behind
 * the page a person is looking at should not be the one thing left holding a
 * pre-write view of this company.
 *
 * `contacts` is deliberately absent. A contact's track and a company's kind
 * are different questions on different tables -- 069 says so out loud -- and
 * a list that includes what the panel cannot have changed is a list nobody
 * can reason about the next time something IS missing from it.
 */
export const COMPANY_PROFILE_INVALIDATES = [
  "company_profile",
  "companies",
] as const;

/**
 * Ask react-query to refetch everything a profile write changed.
 *
 * A FUNCTION RATHER THAN TWO LINES IN A CLICK HANDLER, for the reason
 * invalidateLifecycle is: the browser test cannot prove those lines exist.
 * Deleting the invalidation entirely still passes in Playwright, because its
 * clicks fire window focus events and react-query refetches on focus by
 * default -- so the panel refreshes for a reason unrelated to our code, and
 * one that does not happen to a person sitting still on a company page.
 */
export function invalidateCompanyProfile(
  client: Invalidator,
): Promise<unknown> {
  return Promise.all(
    COMPANY_PROFILE_INVALIDATES.map((resource) =>
      client.invalidateQueries({ queryKey: [resource] }),
    ),
  );
}

/**
 * Write the patch, inserting the row if the company has none.
 *
 * AN UPSERT, BECAUSE ABSENCE IS THE NORMAL STATE: 57 companies, zero profile
 * rows. An update-only write would fail for every company in the database --
 * on the click that was finally recording what the company is.
 *
 * THE EXISTENCE CHECK IS A FRESH READ, not the copy the component holds. A
 * cached "no row" that is one write stale turns an update into a second
 * insert, and company_id is the primary key, so that is a constraint
 * violation on somebody's edit rather than a silent duplicate.
 *
 * `kind` IS REQUIRED ON AN INSERT. The column is NOT NULL with no default, so
 * a first write that did not say what the company is would come back as a
 * Postgres null-violation naming a column the person never saw.
 *
 * `id` is the company id: company_id IS the primary key of that table, and
 * the provider is told so via NON_ID_PRIMARY_KEYS in ./primaryKeys.ts.
 */
export async function saveCompanyProfile(
  dataProvider: DataProvider,
  companyId: Identifier,
  patch: CompanyProfilePatch,
): Promise<void> {
  const { data } = await dataProvider.getList<any>("company_profile", {
    filter: { company_id: companyId },
    pagination: { page: 1, perPage: 1 },
    sort: { field: "company_id", order: "ASC" },
  });
  const existing = data[0];

  if (existing) {
    await dataProvider.update("company_profile", {
      id: companyId,
      data: patch,
      previousData: existing,
    });
    return;
  }

  if (!patch.kind)
    throw new Error(
      "A new company profile has to say what the company is — customer, partner or other. The column is NOT NULL.",
    );

  await dataProvider.create("company_profile", {
    data: { company_id: companyId, ...patch },
  });
}
